import fetchDownload from '../../../services/GetDownload.jsx';
import { DownloadFileFunc, FetchSummaryWithWeightsParams } from '../../../types';
import { buildDownloadEndpoint, buildDownloadFiltersEndpoint } from '../../../utils/buildDownloadEndpoint';
import JSZip from 'jszip';

export async function fetchDownloadFile({
  extension,
  columnVisibilityModel,
  view,
  source = '',
  tab,
  searchItems,
  titleGrid,
  isAuthenticated,
  authorization,
  isEvidence,
  isMapping,
  mappingContent,
}: DownloadFileFunc & {
  filters?: Record<string, any>;
  totalResults?: number;
}): Promise<void> {
  let url = buildDownloadEndpoint({
    extension,
    columnVisibilityModel,
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    mappingContent,
  });

  if (!isAuthenticated) {
    url += '&skey=unauth';
  }

  const blob = await fetchDownload(url, authorization);
  const blobUrl = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = blobUrl;

  const fileName = `${view}_${titleGrid}_${tab}_${source}_${searchItems!
    .slice(0, 5)
    .map(i => i.id)
    .join('-')}.${extension}`;
  link.setAttribute('download', fileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

//TODO: Replace the function above fot this one in TR-116
export async function fetchDownloadFiltersFile({
  extension,
  columnVisibilityModel,
  view,
  source = '',
  tab,
  searchItems,
  titleGrid,
  isAuthenticated,
  authorization,
  isEvidence,
  isMapping,
  mappingContent,
  filters = {},
  totalResults = 0,
  filterValue,
}: DownloadFileFunc & {
  filters?: Record<string, any>;
  totalResults?: number;
}): Promise<void> {
  let url = buildDownloadFiltersEndpoint({
    extension,
    columnVisibilityModel,
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    mappingContent,
    filters,
  });

  if (!isAuthenticated) {
    url += '&skey=unauth';
  }

  const dataBlob = await fetchDownload(url, authorization);

  const searchInfo = createSearchInfoContent({
    searchItems,
    view,
    isEvidence,
    source,
    tab,
    totalResults,
    filterArray: filterValue,
    columnVisibilityModel,
    isMapping,
    mappingContent,
  });

  const zip = new JSZip();

  const maxIds = 5;
  const ids = searchItems!
    .slice(0, maxIds)
    .map(i => i.id)
    .join('-');
  const extra = searchItems!.length > maxIds ? `+${searchItems!.length - maxIds}` : '';

  const dataFileName = `search_result_${ids}${extra}.${extension}`;
  const infoFileName = `search_info_${ids}${extra}.txt`;

  const zipName = `search_${ids}${extra}.zip`;

  zip.file(dataFileName, dataBlob);
  zip.file(infoFileName, searchInfo);

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  const zipUrl = window.URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = zipUrl;
  link.setAttribute('download', zipName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => {
    window.URL.revokeObjectURL(zipUrl);
  }, 1000);
}

function createSearchInfoContent({
  searchItems,
  view,
  isEvidence,
  source,
  tab,
  totalResults,
  filterArray,
  columnVisibilityModel,
  isMapping,
  mappingContent,
}) {
  const formattedFilters = formatFiltersFromArray(filterArray);

  // Format search terms based on dataset type
  const formatSearchTerms = items => {
    if (!items || items.length === 0) return 'N/A';

    return items
      .map(item => {
        const dataset = item.dataset || view;
        switch (dataset) {
          case 'DISEASES':
            return `${item.name || 'N/A'} (CUI: ${item.id})`;
          case 'GENES':
            return `${item.symbol || 'N/A'} (${item.description || 'N/A'}, EntrezId: ${item.ncbiID || item.id})`;
          case 'VARIANTS':
            return `${item.strID || item.id} (Gene: ${item.geneSymbol || 'N/A'})`;
          case 'CHEMICALS':
            return `${item.chemPrefName || 'N/A'} (ID: ${item.chemID || item.id})`;
          default:
            return item.name || item.symbol || item.id || 'N/A';
        }
      })
      .join(', ');
  };

  return `SEARCH INFORMATION
==================

Download Date:       ${new Date().toLocaleString()}

SEARCH DETAILS
--------------
Search Terms:        ${formatSearchTerms(searchItems)}
Analysis Type:       ${view}
Data Type:           ${isEvidence ? 'Evidence' : 'Summary'}
Source:              ${source.replace('_', ' ')}
Tab:                 ${tab}
Total Results:       ${totalResults}

FILTERS APPLIED
---------------
${formattedFilters}

DISPLAY SETTINGS
----------------
Visible Columns:     ${
    columnVisibilityModel
      ? Object.entries(columnVisibilityModel)
          .filter(([_, visible]) => visible)
          .map(([col, _]) => col)
          .join(', ')
      : 'All'
  }

==================`;
}

function formatFiltersFromArray(filterArray) {
  if (!filterArray || filterArray.length === 0) return 'None';

  return filterArray
    .map(({ name, value, operator }) => {
      if (value === null || value === undefined || value === '') return null;

      switch (operator) {
        case 'gt':
          return `${name}: > ${value}`;
        case 'lt':
          return `${name}: < ${value}`;
        case 'between':
          return Array.isArray(value)
            ? `${name}: between ${value[0]} and ${value[1]}`
            : `${name}: between ${value}`;
        case 'equals':
        default:
          return `${name}: ${value}`;
      }
    })
    .filter(Boolean)
    .join('\n');
}

export async function fetchSummaryWithWeights({
  source = 'ALL',
  disease,
  geneSymbol,
  pageNumber = 0,
  pageSize = 10,
  sourceWeights,
  authorization,
}: FetchSummaryWithWeightsParams): Promise<any> {
  const { API_URL } = await import('../../../Components/Constants');

  const params = new URLSearchParams({
    source,
    page_number: pageNumber.toString(),
    page_size: pageSize.toString(),
  });

  if (disease) {
    params.append('disease', disease);
  }

  if (geneSymbol) {
    params.append('gene_symbol', geneSymbol);
  }

  if (sourceWeights.length > 0) {
    const weightsString = sourceWeights.map(sw => `${sw.name}:${sw.score}`).join(',');
    params.append('source_weights', weightsString);
  }

  const url = `${API_URL}api/gda/summary?${params.toString()}`;

  const headers = {
    'Content-Type': 'application/json',
    accept: 'application/json',
    Authorization: authorization,
  };

  const response = await fetch(url, { headers });
  const data = await response.json();

  return data;
}
